import { useState, ChangeEvent, FormEvent, ReactElement } from 'react';
import { FormData, ModalContent, StatesObj, DepartmentsObj, CustomerTypeObj, ProductTypeObj } from '.';
import { useSubmitAcrForm } from '@services/hooks/acr/useAcrFormSubmission';

const AcrForm = (): ReactElement => {
	const [formData, setFormData] = useState<FormData>({
		name: '',
		mobile: '',
		employeeCode: '',
		complaintDate: '',
		department: '',
		otherSpecify: '',
		productType: '',
		state: '',
		city: '',
		productName: '',
		customerType: '',
		address: '',
		sampleDate: '',
	});

	const [files, setFiles] = useState<File[]>([]);
	const [showOtherSpecify, setShowOtherSpecify] = useState<boolean>(false);
	const [productDescription, setProductDescription] = useState<string>('');
	const [productDescriptionBelow, setProductDescriptionBelow] = useState<string>('');
	const [showModal, setShowModal] = useState<boolean>(false);
	const [modalContent, setModalContent] = useState<ModalContent>({ loading: false, success: false });
	const [fileNames, setFileNames] = useState<string>('');
	const submitAcrForm = useSubmitAcrForm();

	const resetForm = (): void => {
		setFormData({
			name: '',
			mobile: '',
			employeeCode: '',
			complaintDate: '',
			department: '',
			otherSpecify: '',
			productType: '',
			state: '',
			city: '',
			productName: '',
			customerType: '',
			address: '',
			sampleDate: '',
		});
		setFiles([]);
		setFileNames('');
		setShowOtherSpecify(false);
		setProductDescription('');
		setProductDescriptionBelow('');
	};

	const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));

		if (name === 'department') {
			setShowOtherSpecify(value === 'Other (Please specify)');
		}

		if (name === 'productType') {
			updateProductDescriptions(value);
		}
	};

	const updateProductDescriptions = (productType: string): void => {
		if (productType === 'Counterfeit') {
			setProductDescription(
				'Counterfeit/Spurious – Goods which are falsely claimed to be genuine products of Pidilite, i.e., the look of the product will be same as or similar to that of Pidilite including the product name logo, Pidilite name etc. It will be difficult to differentiate between Pidilite original product and counterfeit/spurious product.'
			);
			setProductDescriptionBelow(
				'Sending the Sample Product to HO is Mandatory.\n\nAddress for Counterfeit Products – Sandeep Joglekar, Pidilite HO, Ramkrishna Mandir Road, Off Sir Mathuradas Vasanji Road, Andheri, Andheri East, Mumbai, Maharashtra 400059.'
			);
		} else if (productType === 'Copycat') {
			setProductDescription(
				'Copycat – A product that is deceptively similar to a PIL product in terms of logo, trademarks, packaging, design. The Look-alike products will be sold by different Companies with their own name however they might copy our trademark, trade dress and design of the container. Example: ROFF written as Ruff, Dr Fixit written as Dr Fitfix'
			);
			setProductDescriptionBelow(
				'Sending the Sample Product to HO is Mandatory.\n\nAddress for Copycat Products – Asheesh Gupta, Pidilite HO, Ramkrishna Mandir Road, Off Sir Mathuradas Vasanji Road, Andheri, Andheri East, Mumbai, Maharashtra 400059.'
			);
		} else {
			setProductDescription('');
			setProductDescriptionBelow('');
		}
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
		if (e.target.files) {
			const selectedFiles: File[] = Array.from(e.target.files);
			setFiles(selectedFiles);
			const names: string = selectedFiles.map((file) => file.name).join(', ');
			setFileNames(names);
		}
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
		e.preventDefault();

		setShowModal(true);
		setModalContent({ loading: true, success: false });

		try {
			await submitAcrForm.mutateAsync({ formData, files });

			// Success handling
			setModalContent({ loading: false, success: true });

			// Close modal after 5 seconds
			setTimeout(() => {
				setShowModal(false);
				resetForm();
			}, 5000);
		} catch (err) {
			setShowModal(false);
		}
	};

	const today: string = new Date().toISOString().split('T')[0];

	return (
		<div className="brand-protection-survey">
			<div className="bps-container">
				<h1 className="bps-text">Brand Protection Survey</h1>
				<form id="caseForm" onSubmit={handleSubmit}>
					<div className="bps-form-row">
						<div className="bps-input-data">
							<input
								id="name"
								name="name"
								type="text"
								maxLength={50}
								required
								value={formData.name}
								onChange={handleInputChange}
							/>
							<div className="bps-underline"></div>
							<label htmlFor="name">
								Name<span className="bps-required-star">*</span>
							</label>
						</div>
					</div>

					<div className="bps-form-row">
						<div className="bps-input-data">
							<input
								id="mobile"
								name="mobile"
								type="text"
								maxLength={10}
								minLength={10}
								pattern="\d{10}"
								title="Please enter a 10-digit phone number"
								required
								value={formData.mobile}
								onChange={handleInputChange}
							/>
							<div className="bps-underline"></div>
							<label htmlFor="mobile">
								Mobile Number<span className="bps-required-star">*</span>
							</label>
						</div>
						<div className="bps-input-data">
							<input
								id="employeeCode"
								name="employeeCode"
								type="text"
								required
								value={formData.employeeCode}
								onChange={handleInputChange}
							/>
							<div className="bps-underline"></div>
							<label htmlFor="employeeCode">
								Employee Code<span className="bps-required-star">*</span>
							</label>
						</div>
					</div>

					<div className="bps-form-row bps-label-static-container">
						<div className="bps-input-data bps-date-field">
							<input
								type="date"
								id="complaintDate"
								name="complaintDate"
								max={today}
								required
								value={formData.complaintDate}
								onChange={handleInputChange}
							/>
							<div className="bps-underline"></div>
							<label htmlFor="complaintDate">
								Please enter the date of the Complaint<span className="bps-required-star">*</span>
							</label>
						</div>
						<div className="bps-input-data bps-date-field">
							<select
								id="department"
								name="department"
								required
								value={formData.department}
								onChange={handleInputChange}
							>
								<option value="">--None--</option>
								{DepartmentsObj.map((dept: string) => (
									<option key={dept} value={dept}>
										{dept}
									</option>
								))}
							</select>
							<div className="bps-underline"></div>
							<label htmlFor="department">
								Please Select Department/Division<span className="bps-required-star">*</span>
							</label>
						</div>
					</div>

					{showOtherSpecify && (
						<div className="bps-form-row">
							<div className="bps-input-data">
								<input
									type="text"
									name="otherSpecify"
									maxLength={255}
									value={formData.otherSpecify}
									onChange={handleInputChange}
								/>
								<div className="bps-underline"></div>
								<label htmlFor="otherDetails">
									Other (Please specify)<span className="bps-required-star">*</span>
								</label>
							</div>
						</div>
					)}

					<div className="bps-form-row bps-label-static-container">
						<div className="bps-form-product">
							<div className="bps-input-data bps-date-field bps-product-type">
								<select
									id="productType"
									name="productType"
									required
									value={formData.productType}
									onChange={handleInputChange}
								>
									<option value="">--None--</option>
									{ProductTypeObj.map((state: string) => (
										<option key={state} value={state}>
											{state}
										</option>
									))}
								</select>
								<div className="bps-underline"></div>
								<label htmlFor="productType">
									Please select the type of product<span className="bps-required-star">*</span>
								</label>
							</div>
						</div>

						<div className="bps-input-data bps-date-field bps-from-field">
							<select id="state" name="state" required value={formData.state} onChange={handleInputChange}>
								<option value="">--None--</option>
								{StatesObj.map((state: string) => (
									<option key={state} value={state}>
										{state}
									</option>
								))}
							</select>
							<div className="bps-underline"></div>
							<label htmlFor="state">
								Please select the state where the product has been found<span className="bps-required-star">*</span>
							</label>
						</div>
					</div>

					{productDescription && <div className="bps-product-description-message">{productDescription}</div>}

					<div className="bps-form-row">
						<div className="bps-input-data">
							<input type="text" id="city" name="city" required value={formData.city} onChange={handleInputChange} />
							<div className="bps-underline"></div>
							<label htmlFor="city">
								Please enter the city where the product is found<span className="bps-required-star">*</span>
							</label>
						</div>
					</div>

					<div className="bps-form-row">
						<div className="bps-input-data">
							<input
								type="text"
								id="productName"
								name="productName"
								required
								value={formData.productName}
								onChange={handleInputChange}
							/>
							<div className="bps-underline"></div>
							<label htmlFor="productName">
								Please enter the name of the Pidilite product infringed <span className="bps-required-star">*</span>
							</label>
						</div>
					</div>

					<div className="bps-form-row bps-label-static-container">
						<div className="bps-input-data bps-date-field bps-from-field">
							<select
								id="customerType"
								name="customerType"
								required
								value={formData.customerType}
								onChange={handleInputChange}
							>
								<option value="">--None--</option>
								{CustomerTypeObj.map((state: string) => (
									<option key={state} value={state}>
										{state}
									</option>
								))}
							</select>
							<div className="bps-underline"></div>
							<label htmlFor="customerType">
								What is the nature of Business of the dealer?<span className="bps-required-star">*</span>
							</label>
						</div>
					</div>

					<div className="bps-form-row">
						<div className="bps-input-data bps-textarea bps-from-field">
							<textarea
								id="address"
								name="address"
								rows={3}
								required
								value={formData.address}
								onChange={handleInputChange}
							></textarea>
							<div className="bps-underline"></div>
							<label htmlFor="address">
								Please enter the address of the Infringing party<span className="bps-required-star">*</span>
							</label>
						</div>
					</div>

					<div className="bps-form-row bps-label-static-container bps-from-video-field">
						<div className="bps-upload-field">
							<label htmlFor="files">Please upload the photographs / Videos of both sides of the packs</label>
							<input type="file" id="files" name="Attachments" multiple onChange={handleFileChange} />
							<p className="bps-file-name">{fileNames}</p>
							<p className="bps-upload-field-desc">
								{`⚠️ Please ensure If you're uploading multiple files, the combined total size of all files can be up to 2
								GB, but each individual file should still not exceed 35 MB.`}
							</p>
						</div>
					</div>

					<div className="bps-form-row bps-label-static-container">
						<div className="bps-input-data bps-date-field bps-from-field">
							<input
								type="date"
								id="sampleDate"
								name="sampleDate"
								min={today}
								required
								value={formData.sampleDate}
								onChange={handleInputChange}
							/>
							<div className="bps-underline"></div>
							<label htmlFor="sampleDate">
								By when will the sample be sent to Head Office?<span className="bps-required-star">*</span>
							</label>
						</div>
					</div>

					{productDescriptionBelow && (
						<div className="bps-product-description-message">
							{productDescriptionBelow.split('\n').map((line: string) => (
								<div key={line}>{line}</div>
							))}
						</div>
					)}

					<div className="bps-form-row bps-submit-btn">
						<div className="bps-input-data">
							<div className="bps-inner"></div>
							<input type="submit" name="submit" value="Submit" />
						</div>
					</div>
				</form>

				{/* Modal */}
				{showModal && (
					<div className="bps-modal-overlay">
						<div className="bps-modal-content">
							{modalContent.loading && <span>Uploading files, please wait...</span>}
							{modalContent.success && (
								<span className="bps-modal-success">
									Your feedback has been submitted to our Brand Protection team for further action.
								</span>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default AcrForm;
